import {
  ARENA_HEIGHT,
  ARENA_PLATFORMS,
  ARENA_WIDTH,
  CAPTURE_MOVE_SPEED,
  CAPTURE_TICKS,
  COUNTDOWN_TICKS,
  DUEL_TARGET_SCORE,
  EMPTY_INPUT,
  ESCAPE_INVULNERABILITY_TICKS,
  FIRE_COOLDOWN_TICKS,
  FIXED_DELTA_SECONDS,
  GRAVITY,
  INVULNERABILITY_TICKS,
  JUMP_SPEED,
  MAX_FALL_SPEED,
  MAX_WALL_INSET,
  OVERTIME_MAX_PRESSURE_TICKS,
  OVERTIME_WALL_SPEED_PER_TICK,
  OVERTIME_WALL_START_TICKS,
  PLAYER_HALF_HEIGHT,
  PLAYER_HALF_WIDTH,
  PLAYER_MOVE_SPEED,
  POP_LOCK_TICKS,
  PRESSURE_CAPTURE_TICKS,
  PRESSURE_FIRE_COOLDOWN_TICKS,
  PROJECTILE_LIFETIME_TICKS,
  PROJECTILE_RADIUS,
  PROJECTILE_SPAWN_OFFSET,
  PROJECTILE_SPEED,
  RESCUE_INVULNERABILITY_TICKS,
  RESPAWN_TICKS,
  ROUND_END_TICKS,
  ROUND_TICKS,
  ROUNDS_TO_WIN,
  TEAM_TARGET_SCORE,
} from "./constants";
import type {
  ActionInput,
  CreateMatchOptions,
  MatchEvent,
  MatchState,
  Platform,
  PlayerSlot,
  PlayerState,
  TeamId,
} from "./types";

const FLOOR_Y = 660;
const DROP_THROUGH_TICKS = 8;
const CAPTURE_CONTACT_RADIUS = 48;
const CAPTURE_FLOAT_SPEED = 35;
const TOP_CAPTURE_BOUND = 54;

const LEFT_SPAWN_X = [180, 350] as const;
const RIGHT_SPAWN_X = [1_100, 930] as const;

type InputsByPlayer = Readonly<Record<string, ActionInput | undefined>>;

function cloneInput(input: ActionInput): ActionInput {
  return {
    move: input.move,
    jump: input.jump,
    drop: input.drop,
    fire: input.fire,
  };
}

function normalizeInput(input: ActionInput | undefined): ActionInput {
  if (input === undefined) {
    return cloneInput(EMPTY_INPUT);
  }

  return {
    move: input.move < 0 ? -1 : input.move > 0 ? 1 : 0,
    jump: input.jump === true,
    drop: input.drop === true,
    fire: input.fire === true,
  };
}

function otherTeam(team: TeamId): TeamId {
  return team === "blue" ? "orange" : "blue";
}

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function validateSlots(mode: CreateMatchOptions["mode"], slots: PlayerSlot[]): void {
  const expectedSlots = mode === "duel" ? 2 : 4;
  if (slots.length !== expectedSlots) {
    throw new Error(`${mode} matches require exactly ${expectedSlots} slots`);
  }

  const ids = new Set<string>();
  const teamCounts = { blue: 0, orange: 0 };
  for (const slot of slots) {
    if (slot.id.trim().length === 0 || slot.name.trim().length === 0) {
      throw new Error("Player ids and names must not be empty");
    }
    if (ids.has(slot.id)) {
      throw new Error(`Duplicate player id: ${slot.id}`);
    }
    ids.add(slot.id);
    teamCounts[slot.team] += 1;
  }

  const expectedPerTeam = expectedSlots / 2;
  if (
    teamCounts.blue !== expectedPerTeam ||
    teamCounts.orange !== expectedPerTeam
  ) {
    throw new Error(`${mode} matches require balanced teams`);
  }
}

function spawnPosition(
  players: ReadonlyArray<Pick<PlayerState, "id" | "team">>,
  player: Pick<PlayerState, "id" | "team">,
  round: number,
): { x: number; y: number; facing: -1 | 1 } {
  const teammates = players.filter((candidate) => candidate.team === player.team);
  const teamIndex = Math.max(
    0,
    teammates.findIndex((candidate) => candidate.id === player.id),
  );
  const blueStartsLeft = round % 2 === 1;
  const startsLeft = player.team === "blue" ? blueStartsLeft : !blueStartsLeft;
  const positions = startsLeft ? LEFT_SPAWN_X : RIGHT_SPAWN_X;

  return {
    x: positions[Math.min(teamIndex, positions.length - 1)] ?? positions[0],
    y: FLOOR_Y - PLAYER_HALF_HEIGHT,
    facing: startsLeft ? 1 : -1,
  };
}

function makePlayer(slot: PlayerSlot): PlayerState {
  return {
    ...slot,
    x: 0,
    y: FLOOR_Y - PLAYER_HALF_HEIGHT,
    vx: 0,
    vy: 0,
    facing: slot.team === "blue" ? 1 : -1,
    onGround: true,
    standingPlatformId: "floor",
    dropThroughTicks: 0,
    status: "active",
    capturedBy: null,
    captureTicksRemaining: 0,
    popLockTicksRemaining: 0,
    respawnTicksRemaining: 0,
    invulnerabilityTicksRemaining: 0,
    fireCooldownTicks: 0,
    lastInput: cloneInput(EMPTY_INPUT),
  };
}

function placePlayerAtSpawn(state: MatchState, player: PlayerState): void {
  const spawn = spawnPosition(state.players, player, state.round);
  player.x = spawn.x;
  player.y = spawn.y;
  player.vx = 0;
  player.vy = 0;
  player.facing = spawn.facing;
  player.onGround = true;
  player.standingPlatformId = "floor";
  player.dropThroughTicks = 0;
  player.status = "active";
  player.capturedBy = null;
  player.captureTicksRemaining = 0;
  player.popLockTicksRemaining = 0;
  player.respawnTicksRemaining = 0;
  player.fireCooldownTicks = 0;
  player.lastInput = cloneInput(EMPTY_INPUT);
}

function resetPlayersForRound(state: MatchState): void {
  state.projectiles = [];
  for (const player of state.players) {
    placePlayerAtSpawn(state, player);
    player.invulnerabilityTicksRemaining = 0;
  }
}

export function createMatchState(options: CreateMatchOptions): MatchState {
  if (options.id.trim().length === 0) {
    throw new Error("Match id must not be empty");
  }
  validateSlots(options.mode, options.slots);

  const players = options.slots.map(makePlayer);
  const state: MatchState = {
    schemaVersion: 1,
    id: options.id,
    mode: options.mode,
    seed: (options.seed ?? stableHash(options.id)) >>> 0,
    tick: 0,
    phase: "countdown",
    phaseTicksRemaining: COUNTDOWN_TICKS,
    round: 1,
    roundTicksRemaining: ROUND_TICKS,
    goldenPop: false,
    overtimeTicks: 0,
    wallInset: 0,
    targetScore:
      options.mode === "duel" ? DUEL_TARGET_SCORE : TEAM_TARGET_SCORE,
    scores: { blue: 0, orange: 0 },
    roundWins: { blue: 0, orange: 0 },
    roundWinner: null,
    matchWinner: null,
    players,
    projectiles: [],
    platforms: ARENA_PLATFORMS.map((platform) => ({ ...platform })),
    nextEntityId: 1,
  };

  resetPlayersForRound(state);
  return state;
}

export function resetMatchForDemo(state: MatchState): MatchState {
  const fresh = createMatchState({
    id: state.id,
    mode: state.mode,
    slots: state.players.map(({ id, name, team, kind }) => ({
      id,
      name,
      team,
      kind,
    })),
    seed: state.seed,
  });

  Object.assign(state, fresh);
  return state;
}

function syncLastInputs(state: MatchState, inputs: InputsByPlayer): void {
  for (const player of state.players) {
    player.lastInput = normalizeInput(inputs[player.id]);
  }
}

function startNextRound(state: MatchState): void {
  state.round += 1;
  state.phase = "countdown";
  state.phaseTicksRemaining = COUNTDOWN_TICKS;
  state.roundTicksRemaining = ROUND_TICKS;
  state.goldenPop = false;
  state.overtimeTicks = 0;
  state.wallInset = 0;
  state.scores.blue = 0;
  state.scores.orange = 0;
  state.roundWinner = null;
  resetPlayersForRound(state);
}

function beginGoldenPop(state: MatchState, events: MatchEvent[]): void {
  if (state.goldenPop) {
    return;
  }
  state.goldenPop = true;
  state.roundTicksRemaining = 0;
  state.overtimeTicks = 0;
  events.push({ type: "golden_pop_started", tick: state.tick });
}

function finishRound(
  state: MatchState,
  winner: TeamId,
  events: MatchEvent[],
): void {
  state.roundWinner = winner;
  state.roundWins[winner] += 1;
  state.projectiles = [];

  events.push({
    type: "round_ended",
    tick: state.tick,
    round: state.round,
    winner,
    roundWins: { ...state.roundWins },
  });

  if (state.roundWins[winner] >= ROUNDS_TO_WIN) {
    state.phase = "match_end";
    state.phaseTicksRemaining = 0;
    state.matchWinner = winner;
    events.push({
      type: "match_ended",
      tick: state.tick,
      winner,
      roundWins: { ...state.roundWins },
    });
    return;
  }

  state.phase = "round_end";
  state.phaseTicksRemaining = ROUND_END_TICKS;
}

function updatePassiveTimers(player: PlayerState): void {
  player.dropThroughTicks = Math.max(0, player.dropThroughTicks - 1);
  player.invulnerabilityTicksRemaining = Math.max(
    0,
    player.invulnerabilityTicksRemaining - 1,
  );
  player.fireCooldownTicks = Math.max(0, player.fireCooldownTicks - 1);
}

function activeHorizontalBounds(state: MatchState): { left: number; right: number } {
  return {
    left: state.wallInset + PLAYER_HALF_WIDTH,
    right: ARENA_WIDTH - state.wallInset - PLAYER_HALF_WIDTH,
  };
}

function clampPlayerToArena(state: MatchState, player: PlayerState): void {
  const { left, right } = activeHorizontalBounds(state);
  if (player.x < left) {
    player.x = left;
    player.vx = Math.max(0, player.vx);
  } else if (player.x > right) {
    player.x = right;
    player.vx = Math.min(0, player.vx);
  }
}

function chooseLandingPlatform(
  player: PlayerState,
  previousBottom: number,
  nextBottom: number,
  platforms: Platform[],
): Platform | null {
  let landing: Platform | null = null;
  for (const platform of platforms) {
    if (platform.oneWay && player.dropThroughTicks > 0) {
      continue;
    }

    const overlapsHorizontally =
      player.x + PLAYER_HALF_WIDTH > platform.x &&
      player.x - PLAYER_HALF_WIDTH < platform.x + platform.width;
    const crossesTop =
      previousBottom <= platform.y + 0.001 && nextBottom >= platform.y;

    if (
      overlapsHorizontally &&
      crossesTop &&
      (landing === null || platform.y < landing.y)
    ) {
      landing = platform;
    }
  }
  return landing;
}

function updateActivePlayer(
  state: MatchState,
  player: PlayerState,
  input: ActionInput,
  events: MatchEvent[],
): void {
  const wantsDrop =
    input.drop &&
    player.onGround &&
    player.standingPlatformId !== null &&
    state.platforms.find((platform) => platform.id === player.standingPlatformId)
      ?.oneWay === true;

  if (wantsDrop) {
    player.onGround = false;
    player.standingPlatformId = null;
    player.dropThroughTicks = DROP_THROUGH_TICKS;
    player.y += 3;
  }

  const jumpPressed = input.jump && !player.lastInput.jump;
  if (jumpPressed && player.onGround && !input.drop) {
    player.vy = -JUMP_SPEED;
    player.onGround = false;
    player.standingPlatformId = null;
  }

  player.vx = input.move * PLAYER_MOVE_SPEED;
  if (input.move !== 0) {
    player.facing = input.move;
  }

  const previousBottom = player.y + PLAYER_HALF_HEIGHT;
  player.vy = Math.min(
    MAX_FALL_SPEED,
    player.vy + GRAVITY * FIXED_DELTA_SECONDS,
  );
  player.x += player.vx * FIXED_DELTA_SECONDS;
  player.y += player.vy * FIXED_DELTA_SECONDS;
  clampPlayerToArena(state, player);

  player.onGround = false;
  player.standingPlatformId = null;
  if (player.vy >= 0) {
    const landing = chooseLandingPlatform(
      player,
      previousBottom,
      player.y + PLAYER_HALF_HEIGHT,
      state.platforms,
    );
    if (landing !== null) {
      player.y = landing.y - PLAYER_HALF_HEIGHT;
      player.vy = 0;
      player.onGround = true;
      player.standingPlatformId = landing.id;
    }
  }

  if (player.y < PLAYER_HALF_HEIGHT) {
    player.y = PLAYER_HALF_HEIGHT;
    player.vy = Math.max(0, player.vy);
  }

  if (player.y > ARENA_HEIGHT + PLAYER_HALF_HEIGHT) {
    placePlayerAtSpawn(state, player);
    player.invulnerabilityTicksRemaining = INVULNERABILITY_TICKS;
  }

  if (input.fire && player.fireCooldownTicks === 0) {
    if (player.invulnerabilityTicksRemaining > 0) {
      player.invulnerabilityTicksRemaining = 0;
    }
    const projectileId = state.nextEntityId;
    state.nextEntityId += 1;
    state.projectiles.push({
      id: projectileId,
      ownerId: player.id,
      team: player.team,
      x: player.x + player.facing * PROJECTILE_SPAWN_OFFSET,
      y: player.y - 5,
      vx: player.facing * PROJECTILE_SPEED,
      vy: 0,
      radius: PROJECTILE_RADIUS,
      ageTicks: 0,
    });
    player.fireCooldownTicks =
      state.overtimeTicks >= OVERTIME_MAX_PRESSURE_TICKS
        ? PRESSURE_FIRE_COOLDOWN_TICKS
        : FIRE_COOLDOWN_TICKS;
    events.push({
      type: "fired",
      tick: state.tick,
      playerId: player.id,
      projectileId,
    });
  }
}

function updateCapturedPlayer(
  state: MatchState,
  player: PlayerState,
  input: ActionInput,
  events: MatchEvent[],
): void {
  player.vx = input.move * CAPTURE_MOVE_SPEED;
  player.vy = -CAPTURE_FLOAT_SPEED;
  player.x += player.vx * FIXED_DELTA_SECONDS;
  player.y += player.vy * FIXED_DELTA_SECONDS;
  player.y = Math.max(TOP_CAPTURE_BOUND, player.y);
  clampPlayerToArena(state, player);

  player.popLockTicksRemaining = Math.max(0, player.popLockTicksRemaining - 1);
  player.captureTicksRemaining = Math.max(0, player.captureTicksRemaining - 1);
  if (player.captureTicksRemaining === 0) {
    player.status = "active";
    player.capturedBy = null;
    player.vx = 0;
    player.vy = 0;
    player.invulnerabilityTicksRemaining = ESCAPE_INVULNERABILITY_TICKS;
    events.push({ type: "escaped", tick: state.tick, playerId: player.id });
  }
}

function updateRespawningPlayer(
  state: MatchState,
  player: PlayerState,
  events: MatchEvent[],
): void {
  player.respawnTicksRemaining = Math.max(0, player.respawnTicksRemaining - 1);
  if (player.respawnTicksRemaining > 0) {
    return;
  }

  placePlayerAtSpawn(state, player);
  player.invulnerabilityTicksRemaining = INVULNERABILITY_TICKS;
  events.push({ type: "respawned", tick: state.tick, playerId: player.id });
}

function projectileHitsPlayer(
  projectile: MatchState["projectiles"][number],
  player: PlayerState,
): boolean {
  const closestX = Math.max(
    player.x - PLAYER_HALF_WIDTH,
    Math.min(projectile.x, player.x + PLAYER_HALF_WIDTH),
  );
  const closestY = Math.max(
    player.y - PLAYER_HALF_HEIGHT,
    Math.min(projectile.y, player.y + PLAYER_HALF_HEIGHT),
  );
  const dx = projectile.x - closestX;
  const dy = projectile.y - closestY;
  return dx * dx + dy * dy <= projectile.radius * projectile.radius;
}

function updateProjectiles(state: MatchState, events: MatchEvent[]): void {
  const removed = new Set<number>();
  const { left, right } = activeHorizontalBounds(state);

  for (const projectile of state.projectiles) {
    projectile.x += projectile.vx * FIXED_DELTA_SECONDS;
    projectile.y += projectile.vy * FIXED_DELTA_SECONDS;
    projectile.ageTicks += 1;

    if (
      projectile.ageTicks > PROJECTILE_LIFETIME_TICKS ||
      projectile.x + projectile.radius < left ||
      projectile.x - projectile.radius > right ||
      projectile.y + projectile.radius < 0 ||
      projectile.y - projectile.radius > ARENA_HEIGHT
    ) {
      removed.add(projectile.id);
      continue;
    }

    const target = state.players.find(
      (player) =>
        player.team !== projectile.team &&
        player.status === "active" &&
        player.invulnerabilityTicksRemaining === 0 &&
        projectileHitsPlayer(projectile, player),
    );
    if (target === undefined) {
      continue;
    }

    target.status = "captured";
    target.capturedBy = projectile.ownerId;
    target.captureTicksRemaining =
      state.overtimeTicks >= OVERTIME_MAX_PRESSURE_TICKS
        ? PRESSURE_CAPTURE_TICKS
        : CAPTURE_TICKS;
    target.popLockTicksRemaining = POP_LOCK_TICKS;
    target.vx = 0;
    target.vy = 0;
    target.onGround = false;
    target.standingPlatformId = null;
    removed.add(projectile.id);
    events.push({
      type: "captured",
      tick: state.tick,
      playerId: target.id,
      byPlayerId: projectile.ownerId,
    });
  }

  if (removed.size > 0) {
    state.projectiles = state.projectiles.filter(
      (projectile) => !removed.has(projectile.id),
    );
  }
}

function playersTouch(a: PlayerState, b: PlayerState): boolean {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy <= CAPTURE_CONTACT_RADIUS * CAPTURE_CONTACT_RADIUS;
}

function processCaptureContacts(
  state: MatchState,
  events: MatchEvent[],
): TeamId[] {
  const scoringTeams: TeamId[] = [];

  for (const captive of state.players) {
    if (captive.status !== "captured") {
      continue;
    }

    const touching = state.players.filter(
      (player) =>
        player.id !== captive.id &&
        player.status === "active" &&
        playersTouch(captive, player),
    );

    const rescuer = touching.find((player) => player.team === captive.team);
    if (rescuer !== undefined) {
      captive.status = "active";
      captive.capturedBy = null;
      captive.captureTicksRemaining = 0;
      captive.popLockTicksRemaining = 0;
      captive.vx = 0;
      captive.vy = 0;
      captive.invulnerabilityTicksRemaining = RESCUE_INVULNERABILITY_TICKS;
      events.push({
        type: "rescued",
        tick: state.tick,
        playerId: captive.id,
        byPlayerId: rescuer.id,
      });
      continue;
    }

    if (captive.popLockTicksRemaining > 0) {
      continue;
    }
    const popper = touching.find((player) => player.team !== captive.team);
    if (popper === undefined) {
      continue;
    }

    captive.status = "respawning";
    captive.capturedBy = null;
    captive.captureTicksRemaining = 0;
    captive.popLockTicksRemaining = 0;
    captive.respawnTicksRemaining = RESPAWN_TICKS;
    captive.vx = 0;
    captive.vy = 0;
    const scoringTeam = otherTeam(captive.team);
    state.scores[scoringTeam] += 1;
    scoringTeams.push(scoringTeam);
    events.push({
      type: "popped",
      tick: state.tick,
      playerId: captive.id,
      byPlayerId: popper.id,
      team: scoringTeam,
      score: state.scores[scoringTeam],
    });
  }

  return scoringTeams;
}

function updateOvertime(state: MatchState, events: MatchEvent[]): void {
  state.overtimeTicks += 1;

  if (state.overtimeTicks === OVERTIME_WALL_START_TICKS) {
    events.push({ type: "overtime_pressure", tick: state.tick, level: 1 });
  }
  if (state.overtimeTicks === OVERTIME_MAX_PRESSURE_TICKS) {
    events.push({ type: "overtime_pressure", tick: state.tick, level: 2 });
  }

  state.wallInset = Math.min(
    MAX_WALL_INSET,
    Math.max(0, state.overtimeTicks - OVERTIME_WALL_START_TICKS) *
      OVERTIME_WALL_SPEED_PER_TICK,
  );
  for (const player of state.players) {
    clampPlayerToArena(state, player);
  }
}

function resolveRoundProgress(
  state: MatchState,
  scoringTeams: TeamId[],
  events: MatchEvent[],
): void {
  if (state.goldenPop) {
    const uniqueScoringTeams = new Set(scoringTeams);
    if (uniqueScoringTeams.size === 1) {
      const winner = uniqueScoringTeams.values().next().value as TeamId;
      finishRound(state, winner, events);
      return;
    }
    updateOvertime(state, events);
    return;
  }

  const blueReached = state.scores.blue >= state.targetScore;
  const orangeReached = state.scores.orange >= state.targetScore;
  if (blueReached || orangeReached) {
    if (state.scores.blue === state.scores.orange) {
      beginGoldenPop(state, events);
    } else {
      finishRound(
        state,
        state.scores.blue > state.scores.orange ? "blue" : "orange",
        events,
      );
    }
    return;
  }

  state.roundTicksRemaining = Math.max(0, state.roundTicksRemaining - 1);
  if (state.roundTicksRemaining > 0) {
    return;
  }

  if (state.scores.blue === state.scores.orange) {
    beginGoldenPop(state, events);
  } else {
    finishRound(
      state,
      state.scores.blue > state.scores.orange ? "blue" : "orange",
      events,
    );
  }
}

export function stepMatch(
  state: MatchState,
  inputsByPlayer: InputsByPlayer,
): MatchEvent[] {
  const events: MatchEvent[] = [];
  state.tick += 1;

  if (state.phase === "match_end") {
    syncLastInputs(state, inputsByPlayer);
    return events;
  }

  if (state.phase === "round_end") {
    state.phaseTicksRemaining = Math.max(0, state.phaseTicksRemaining - 1);
    syncLastInputs(state, inputsByPlayer);
    if (state.phaseTicksRemaining === 0) {
      startNextRound(state);
    }
    return events;
  }

  if (state.phase === "countdown") {
    state.phaseTicksRemaining = Math.max(0, state.phaseTicksRemaining - 1);
    syncLastInputs(state, inputsByPlayer);
    if (state.phaseTicksRemaining === 0) {
      state.phase = "playing";
      events.push({ type: "round_started", tick: state.tick, round: state.round });
    }
    return events;
  }

  for (const player of state.players) {
    const input = normalizeInput(inputsByPlayer[player.id]);
    updatePassiveTimers(player);
    if (player.status === "active") {
      updateActivePlayer(state, player, input, events);
    } else if (player.status === "captured") {
      updateCapturedPlayer(state, player, input, events);
    } else {
      updateRespawningPlayer(state, player, events);
    }
    player.lastInput = input;
  }

  updateProjectiles(state, events);
  const scoringTeams = processCaptureContacts(state, events);
  resolveRoundProgress(state, scoringTeams, events);
  return events;
}
